import { useCallback } from "react";

import { useGenerateAiInterviewQuestionFollowupsMutation } from "../../graphql";
import { useAiInterviewerContext } from "./AiInterviewerContext";
import { useAiInterviewerFormContext } from "./useAiInterviewerFormContext";

const useGenerateQuestionFollowups = (
  questionIndex: number
): {
  // if followupIdx is provided, we regenerate a single follow-up
  generateQuestionFollowups: (followupIdx?: number) => Promise<void>;
  loading: boolean;
} => {
  const aiInterviewer = useAiInterviewerContext();
  const { getValues, setValue } = useAiInterviewerFormContext();
  const [generateFollowupsMutation, { loading }] =
    useGenerateAiInterviewQuestionFollowupsMutation();

  const generateQuestionFollowups = useCallback(
    async (followupIdx?: number) => {
      const formValues = getValues();
      const variables = {
        aiInterviewerId: aiInterviewer.id,
        aiInterviewerName: formValues.name,
        questions: formValues.questions.map(
          ({ sortOrder, __typename, originalQuestionId, ...rest }) => ({
            ...rest,
          })
        ),
        positionInfo: formValues.positionInfo,
        introduction: formValues.introduction,
        closing: formValues.closing,
        jobDescription: formValues.jobDescription,
        targetQuestionPosition: questionIndex,
        onlyOne: typeof followupIdx === "number",
      };
      const result = await generateFollowupsMutation({ variables });
      const followups =
        result.data?.generateAiInterviewQuestionFollowups?.followups;
      if (followups && followups.length > 0) {
        const fieldPath = `questions.${questionIndex}.followups` as const;
        const existing = getValues(fieldPath) ?? [];
        if (typeof followupIdx === "number") {
          const [replacement] = followups;
          existing[followupIdx] = replacement;
          setValue(fieldPath, existing, {
            shouldDirty: true,
          });
        } else {
          // merge & deduplicate, keep order (existing first)
          const merged = [...existing, ...followups].filter(
            (q, idx, arr) => q && arr.indexOf(q) === idx
          );
          setValue(fieldPath, merged, {
            shouldDirty: true,
          });
        }
      }
    },
    [
      aiInterviewer.id,
      getValues,
      setValue,
      questionIndex,
      generateFollowupsMutation,
    ]
  );

  return { generateQuestionFollowups, loading };
};

export default useGenerateQuestionFollowups;
